/**
 * Base Skeleton Component
 * Provides consistent shimmer animation and styling
 * All skeleton components extend this
 */

import React from 'react';
import { styled, keyframes } from '@mui/material/styles';
import { Box } from '@mui/material';
import { loadingConfig } from './loadingConfig';
import useReducedMotion from '../../hooks/useReducedMotion';

// Shimmer animation
const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

// Pulse animation (for reduced motion)
const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
`;

/**
 * Styled skeleton element with shimmer effect
 */
const SkeletonElement = styled(Box)(({ reducedMotion, variant = 'rectangular' }) => {
  const { colors, animation, radius } = loadingConfig;

  // Determine border radius based on variant
  let borderRadius = radius.text;
  if (variant === 'circular') borderRadius = radius.avatar;
  if (variant === 'rounded') borderRadius = radius.card;
  if (variant === 'text') borderRadius = radius.text;

  return {
    backgroundColor: colors.skeleton.base,
    borderRadius,
    position: 'relative',
    overflow: 'hidden',

    // Animation based on motion preference
    ...(reducedMotion
      ? {
          // Pulse for reduced motion
          animation: `${pulse} ${animation.pulse.duration} ${animation.pulse.easing} infinite`,
        }
      : {
          // Shimmer for normal motion
          backgroundImage: animation.shimmer.gradient,
          backgroundSize: '1000px 100%',
          animation: `${shimmer} ${animation.shimmer.duration} ${animation.shimmer.easing} infinite`,
        }),
  };
});

/**
 * Base Skeleton Component
 *
 * @param {Object} props
 * @param {string} props.variant - 'text' | 'circular' | 'rectangular' | 'rounded'
 * @param {string|number} props.width - Width (CSS value or number in px)
 * @param {string|number} props.height - Height (CSS value or number in px)
 * @param {string} props.animation - 'shimmer' | 'pulse' | 'none'
 * @param {Object} props.sx - Additional MUI sx styles
 */
export default function SkeletonBase({
  variant = 'rectangular',
  width,
  height,
  animation = 'shimmer',
  sx = {},
  ...props
}) {
  const reducedMotion = useReducedMotion();

  // Convert numeric values to px
  const widthValue = typeof width === 'number' ? `${width}px` : width;
  const heightValue = typeof height === 'number' ? `${height}px` : height;

  return (
    <SkeletonElement
      variant={variant}
      reducedMotion={reducedMotion || animation === 'pulse'}
      sx={{
        width: widthValue || '100%',
        height: heightValue || '20px',
        ...sx,
      }}
      role="status"
      aria-label={loadingConfig.a11y.labels.loading}
      {...props}
    />
  );
}

/**
 * Text Skeleton - For text lines
 */
export function SkeletonText({ size = 'md', width = '100%', ...props }) {
  const { sizes } = loadingConfig;
  const height = sizes.skeleton.text[size];

  return <SkeletonBase variant="text" width={width} height={height} {...props} />;
}

/**
 * Avatar Skeleton - For circular avatars/icons
 */
export function SkeletonAvatar({ size = 'md', ...props }) {
  const { sizes } = loadingConfig;
  const dimension = sizes.skeleton.avatar[size];

  return <SkeletonBase variant="circular" width={dimension} height={dimension} {...props} />;
}

/**
 * Button Skeleton - For button placeholders
 */
export function SkeletonButton({ size = 'md', width = 120, ...props }) {
  const { sizes } = loadingConfig;
  const height = sizes.skeleton.button[size];

  return <SkeletonBase variant="rounded" width={width} height={height} {...props} />;
}

/**
 * Card Skeleton - For card placeholders
 */
export function SkeletonCard({ height = 'md', ...props }) {
  const { sizes } = loadingConfig;
  const cardHeight = sizes.skeleton.card[height];

  return <SkeletonBase variant="rounded" height={cardHeight} {...props} />;
}
